import { useEffect, useMemo, useState } from 'react';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `${label} failed (${response.status}): ${
        detail || response.statusText
      }`
    );
  }

  const text = await response.text();

  return text ? JSON.parse(text) : null;
}

async function fetchCourseInstructors(courseId) {
  const [courseResponse, accessResponse] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=instructor_id&limit=1`,
        { headers: apiHeaders() }
      ),
      fetch(
        `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&select=instructor_id`,
        { headers: apiHeaders() }
      )
    ]);

  const courseRows = await readJson(
    courseResponse,
    'Loading primary instructor'
  );

  const accessRows = await readJson(
    accessResponse,
    'Loading additional instructors'
  );

  const primaryId =
    courseRows?.[0]?.instructor_id || null;

  const instructorIds = [
    primaryId,
    ...(Array.isArray(accessRows)
      ? accessRows.map(row => row.instructor_id)
      : [])
  ]
    .filter(Boolean)
    .filter(
      (id, index, values) =>
        values.indexOf(id) === index
    );

  if (!instructorIds.length) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=in.(${instructorIds.join(
      ','
    )})&select=id,name,email`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading instructors'
  );

  const byId = new Map(
    (Array.isArray(rows) ? rows : []).map(
      instructor => [
        instructor.id,
        instructor
      ]
    )
  );

  const primary = primaryId
    ? byId.get(primaryId)
    : null;

  const secondary = instructorIds
    .filter(id => id !== primaryId)
    .map(id => byId.get(id))
    .filter(Boolean)
    .sort((a, b) =>
      String(a.name || a.email || '').localeCompare(
        String(b.name || b.email || '')
      )
    );

  return [
    primary,
    ...secondary
  ].filter(Boolean);
}

async function fetchEnrolments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=id,student_id,assigned_instructor_id,students(id,student_code,english_name,chinese_name,is_master)&order=created_at.asc`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading students'
  );

  return (Array.isArray(rows) ? rows : [])
    .filter(
      enrolment =>
        enrolment?.students?.is_master !== true
    )
    .sort((a, b) =>
      String(
        a.students?.student_code || ''
      ).localeCompare(
        String(
          b.students?.student_code || ''
        ),
        undefined,
        {
          numeric: true,
          sensitivity: 'base'
        }
      )
    );
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=id,week_number,class_date,attendance_enabled,pd_enabled,pd_assigned_instructor_id,teaching_instructor_id,course_week_instructors(instructor_id)&order=week_number.asc`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading course weeks'
  );
}


async function fetchAttendanceMemberships(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?select=attendance_group_id,enrolment_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading attendance group memberships'
  );
}


async function fetchAttendanceGroupWeeks(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?select=attendance_group_id,course_week_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading attendance group schedule'
  );
}


async function fetchInstitutionHolidayDates(
  courseId
) {
  const courseResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=instructor_id&limit=1`,
    { headers: apiHeaders() }
  );

  const courseRows = await readJson(
    courseResponse,
    'Loading course institution'
  );

  const primaryInstructorId =
    courseRows?.[0]?.instructor_id || null;

  if (!primaryInstructorId) {
    return [];
  }

  const instructorResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${primaryInstructorId}&select=institution_id&limit=1`,
    { headers: apiHeaders() }
  );

  const instructorRows = await readJson(
    instructorResponse,
    'Loading instructor institution'
  );

  const institutionId =
    instructorRows?.[0]?.institution_id || null;

  if (!institutionId) {
    return [];
  }

  const holidayResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&is_personal=eq.false&select=date`,
    { headers: apiHeaders() }
  );

  const holidayRows = await readJson(
    holidayResponse,
    'Loading institution holidays'
  );

  return (
    Array.isArray(holidayRows)
      ? holidayRows
      : []
  )
    .map(row =>
      String(row.date || '').slice(0, 10)
    )
    .filter(Boolean);
}

async function fetchAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(id,name,requires_submission)&order=instance_number.asc`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading assignments'
  );
}

async function fetchAssignmentTypeSettings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=assignment_type_id,menu_order`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading assignment type order'
  );
}

async function patchRow(
  table,
  id,
  data,
  label
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `${label} failed (${response.status}): ${
        detail || response.statusText
      }`
    );
  }
}

async function replaceClassInstructorRows(
  weekId,
  instructorIds
) {
  const deleteResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_instructors?course_week_id=eq.${weekId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!deleteResponse.ok) {
    const detail = await deleteResponse.text();

    throw new Error(
      `Updating class instructors failed (${deleteResponse.status}): ${
        detail || deleteResponse.statusText
      }`
    );
  }

  if (!instructorIds.length) {
    return;
  }

  const insertResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_instructors`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(
        instructorIds.map(instructorId => ({
          course_week_id: weekId,
          instructor_id: instructorId
        }))
      )
    }
  );

  if (!insertResponse.ok) {
    const detail = await insertResponse.text();

    throw new Error(
      `Updating class instructors failed (${insertResponse.status}): ${
        detail || insertResponse.statusText
      }`
    );
  }
}

function getStudentName(student) {
  return (
    student?.english_name ||
    student?.chinese_name ||
    student?.student_code ||
    '—'
  );
}

function getAssignmentType(assignment) {
  const relation =
    assignment?.assignment_types;

  if (Array.isArray(relation)) {
    return relation[0] || null;
  }

  return relation || null;
}

function getAssignmentLabel(
  assignment,
  allAssignments = []
) {
  const explicit =
    assignment?.title ||
    assignment?.name;

  const baseLabel =
    explicit ||
    getAssignmentType(assignment)?.name ||
    'Assignment';

  const instanceNumber =
    Number(assignment?.instance_number) || 1;

  const sameTypeCount =
    allAssignments.filter(
      item =>
        item.assignment_type_id ===
        assignment.assignment_type_id
    ).length;

  return sameTypeCount > 1
    ? `${baseLabel} ${instanceNumber}`
    : baseLabel;
}

function formatDate(value) {
  if (!value) return '—';

  const parts =
    String(value)
      .slice(0, 10)
      .split('-')
      .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return value;
  }

  return new Date(
    parts[0],
    parts[1] - 1,
    parts[2]
  ).toLocaleDateString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short'
    }
  );
}

export default function InstructorResponsibilities({
  course
}) {
  const [instructors, setInstructors] =
    useState([]);

  const [enrolments, setEnrolments] =
    useState([]);

  const [weeks, setWeeks] =
    useState([]);

  const [
    attendanceMemberships,
    setAttendanceMemberships
  ] = useState([]);

  const [
    attendanceGroupWeeks,
    setAttendanceGroupWeeks
  ] = useState([]);

    const [
  institutionHolidayDates,
  setInstitutionHolidayDates
] = useState([]);

  const [assignments, setAssignments] =
    useState([]);

    const [assignmentTypeSettings, setAssignmentTypeSettings] =
  useState([]);

  const [activeTab, setActiveTab] =
    useState('students');

  const [loading, setLoading] =
    useState(true);

  const [savingKey, setSavingKey] =
    useState('');

  const [error, setError] =
    useState('');

  const [success, setSuccess] =
    useState('');

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');
    setSuccess('');
    setActiveTab('students');

    Promise.all([
  fetchCourseInstructors(course.id),
  fetchEnrolments(course.id),
  fetchWeeks(course.id),
  fetchAssignments(course.id),
  fetchAssignmentTypeSettings(course.id),
  fetchInstitutionHolidayDates(course.id),
  fetchAttendanceMemberships(course.id),
  fetchAttendanceGroupWeeks(course.id)
])
  .then(([
  instructorRows,
  enrolmentRows,
  weekRows,
  assignmentRows,
  assignmentTypeSettingRows,
  institutionHolidayRows,
  attendanceMembershipRows,
  attendanceGroupWeekRows
]) => {
        if (cancelled) return;

        setInstructors(
          Array.isArray(instructorRows)
            ? instructorRows
            : []
        );

        setEnrolments(
          Array.isArray(enrolmentRows)
            ? enrolmentRows
            : []
        );

        setAttendanceMemberships(
          Array.isArray(
            attendanceMembershipRows
          )
            ? attendanceMembershipRows
            : []
        );

        setAttendanceGroupWeeks(
          Array.isArray(
            attendanceGroupWeekRows
          )
            ? attendanceGroupWeekRows
            : []
        );

        setWeeks(
          Array.isArray(weekRows)
            ? weekRows
            : []
        );

setInstitutionHolidayDates(
  Array.isArray(institutionHolidayRows)
    ? institutionHolidayRows
    : []
);

        setAssignments(
          Array.isArray(assignmentRows)
            ? assignmentRows
            : []
        );
setAssignmentTypeSettings(
  Array.isArray(assignmentTypeSettingRows)
    ? assignmentTypeSettingRows
    : []
);

      })
      .catch(err => {
        if (cancelled) return;

        console.error(
          'Instructor responsibilities load failed:',
          err
        );

        setError(
          err?.message ||
            'Failed to load instructor responsibilities.'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

const submissionAssignments =
  useMemo(() => {
    const menuOrderByType = new Map(
      assignmentTypeSettings.map(row => [
        row.assignment_type_id,
        row.menu_order === null ||
        row.menu_order === undefined ||
        row.menu_order === ''
          ? null
          : Number(row.menu_order)
      ])
    );

    const firstTypeIndex = new Map();

    assignments.forEach((assignment, index) => {
      if (
        !firstTypeIndex.has(
          assignment.assignment_type_id
        )
      ) {
        firstTypeIndex.set(
          assignment.assignment_type_id,
          index
        );
      }
    });

    return assignments
      .filter(
        assignment =>
          getAssignmentType(assignment)
            ?.requires_submission === true
      )
      .sort((a, b) => {
        if (
          a.assignment_type_id ===
          b.assignment_type_id
        ) {
          return (
            (Number(a.instance_number) || 0) -
            (Number(b.instance_number) || 0)
          );
        }

        const orderA =
          menuOrderByType.get(
            a.assignment_type_id
          );

        const orderB =
          menuOrderByType.get(
            b.assignment_type_id
          );

        const hasOrderA =
          Number.isFinite(orderA);

        const hasOrderB =
          Number.isFinite(orderB);

        if (hasOrderA && hasOrderB) {
          return orderA - orderB;
        }

        if (hasOrderA) return -1;
        if (hasOrderB) return 1;

        return (
          (firstTypeIndex.get(
            a.assignment_type_id
          ) ?? 0) -
          (firstTypeIndex.get(
            b.assignment_type_id
          ) ?? 0)
        );
      });
  }, [
    assignments,
    assignmentTypeSettings
  ]);

const classWeeks =
  useMemo(() => {
    const holidayDates =
      new Set(
        institutionHolidayDates
      );

    const activeEnrolmentIds =
      new Set(
        enrolments.map(
          enrolment =>
            String(enrolment.id)
        )
      );

    /*
     * Attendance is authoritative for the
     * Classes list.
     *
     * In a normal course, the course week
     * must have attendance enabled.
     *
     * With Group-Based Attendance, the week
     * appears only when at least one active
     * student belongs to an attendance group
     * scheduled for that week.
     */
    const scheduledGroupIdsByWeek =
      new Map();

    if (
      course.group_based_attendance ===
      true
    ) {
      attendanceGroupWeeks.forEach(
        row => {
          const weekId =
            String(
              row.course_week_id
            );

          if (
            !scheduledGroupIdsByWeek.has(
              weekId
            )
          ) {
            scheduledGroupIdsByWeek.set(
              weekId,
              new Set()
            );
          }

          scheduledGroupIdsByWeek
            .get(weekId)
            .add(
              String(
                row.attendance_group_id
              )
            );
        }
      );
    }

    return weeks.filter(week => {
      const classDate =
        String(
          week.class_date || ''
        ).slice(0, 10);

      if (
        !classDate ||
        holidayDates.has(classDate)
      ) {
        return false;
      }

      if (
        course.group_based_attendance !==
        true
      ) {
        return (
          week.attendance_enabled ===
          true
        );
      }

      const scheduledGroupIds =
        scheduledGroupIdsByWeek.get(
          String(week.id)
        );

      if (
        !scheduledGroupIds ||
        scheduledGroupIds.size === 0
      ) {
        return false;
      }

      return attendanceMemberships.some(
        membership =>
          activeEnrolmentIds.has(
            String(
              membership.enrolment_id
            )
          ) &&
          scheduledGroupIds.has(
            String(
              membership
                .attendance_group_id
            )
          )
      );
    });
  }, [
    weeks,
    institutionHolidayDates,
    enrolments,
    attendanceMemberships,
    attendanceGroupWeeks,
    course.group_based_attendance
  ]);

  const pdWeeks =
    useMemo(
      () =>
        weeks.filter(
          week => week.pd_enabled === true
        ),
      [weeks]
    );

  const tabs =
    useMemo(() => {
      const result = [
        {
          key: 'students',
          label: 'Students'
        },
        {
          key: 'classes',
          label: 'Classes'
        }
      ];

      if (course.pd_enabled === true) {
        result.push({
          key: 'pd',
          label: 'Philosophical Diaries'
        });
      }

      if (submissionAssignments.length > 0) {
        result.push({
          key: 'assignments',
          label: 'Assignments'
        });
      }

      return result;
    }, [
      course.pd_enabled,
      submissionAssignments.length
    ]);

  useEffect(() => {
    if (
      !tabs.some(tab => tab.key === activeTab)
    ) {
      setActiveTab(
        tabs[0]?.key || 'students'
      );
    }
  }, [activeTab, tabs]);

  async function saveStudentInstructor(
    enrolmentId,
    instructorId
  ) {
    setSavingKey(
      `student-${enrolmentId}`
    );

    setError('');
    setSuccess('');

    const value =
      instructorId || null;

    try {
      await patchRow(
        'enrolments',
        enrolmentId,
        {
          assigned_instructor_id: value
        },
        'Updating student instructor'
      );

      setEnrolments(prev =>
        prev.map(enrolment =>
          enrolment.id === enrolmentId
            ? {
                ...enrolment,
                assigned_instructor_id:
                  value
              }
            : enrolment
        )
      );

      setSuccess('Saved.');
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          'Failed to update student instructor.'
      );
    } finally {
      setSavingKey('');
    }
  }

  function getClassInstructorIds(week) {
  const assignedRows =
    Array.isArray(week.course_week_instructors)
      ? week.course_week_instructors
      : [];

  // No stored rows means every course instructor.
  if (assignedRows.length === 0) {
    return instructors.map(
      instructor => instructor.id
    );
  }

  return assignedRows
    .map(row => row.instructor_id)
    .filter(Boolean);
}

async function saveClassInstructors(
  weekId,
  selectedInstructorIds
) {
  const validIds =
    instructors
      .map(instructor => instructor.id)
      .filter(id =>
        selectedInstructorIds.includes(id)
      );

  if (validIds.length === 0) {
    setError(
      'At least one instructor must be assigned to a class.'
    );
    return;
  }

  setSavingKey(`class-${weekId}`);
  setError('');
  setSuccess('');

  const allSelected =
    validIds.length === instructors.length;

  // No junction rows = all instructors.
  const storedIds =
    allSelected ? [] : validIds;

  // Keep the old column approximately synchronized
  // while older Instructor App builds may still use it.
  const legacyInstructorId =
    validIds.length === 1
      ? validIds[0]
      : null;

  try {
    await replaceClassInstructorRows(
      weekId,
      storedIds
    );

    await patchRow(
      'course_weeks',
      weekId,
      {
        teaching_instructor_id:
          legacyInstructorId
      },
      'Updating legacy class instructor'
    );

    setWeeks(prev =>
      prev.map(week =>
        week.id === weekId
          ? {
              ...week,
              teaching_instructor_id:
                legacyInstructorId,
              course_week_instructors:
                storedIds.map(
                  instructorId => ({
                    instructor_id:
                      instructorId
                  })
                )
            }
          : week
      )
    );

    setSuccess('Saved.');
  } catch (err) {
    console.error(err);

    setError(
      err?.message ||
        'Failed to update class instructors.'
    );
  } finally {
    setSavingKey('');
  }
}

function toggleClassInstructor(
  week,
  instructorId,
  checked
) {
  const currentIds =
    getClassInstructorIds(week);

  const nextIds = checked
    ? Array.from(
        new Set([
          ...currentIds,
          instructorId
        ])
      )
    : currentIds.filter(
        id => id !== instructorId
      );

  if (nextIds.length === 0) {
    setError(
      'At least one instructor must remain assigned to the class.'
    );
    return;
  }

  saveClassInstructors(
    week.id,
    nextIds
  );
}

  async function savePdInstructor(
    weekId,
    instructorId
  ) {
    setSavingKey(
      `pd-${weekId}`
    );

    setError('');
    setSuccess('');

    const value =
      instructorId || null;

    try {
      await patchRow(
        'course_weeks',
        weekId,
        {
          pd_assigned_instructor_id: value
        },
        'Updating Philosophical Diary instructor'
      );

      setWeeks(prev =>
        prev.map(week =>
          week.id === weekId
            ? {
                ...week,
                pd_assigned_instructor_id:
                  value
              }
            : week
        )
      );

      setSuccess('Saved.');
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          'Failed to update Philosophical Diary instructor.'
      );
    } finally {
      setSavingKey('');
    }
  }

  async function saveAssignmentInstructor(
    assignmentId,
    instructorId
  ) {
    setSavingKey(
      `assignment-${assignmentId}`
    );

    setError('');
    setSuccess('');

    const value =
      instructorId || null;

    try {
      await patchRow(
        'course_assignments',
        assignmentId,
        {
          assigned_instructor_id: value
        },
        'Updating assignment instructor'
      );

      setAssignments(prev =>
        prev.map(assignment =>
          assignment.id === assignmentId
            ? {
                ...assignment,
                assigned_instructor_id:
                  value
              }
            : assignment
        )
      );

      setSuccess('Saved.');
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          'Failed to update assignment instructor.'
      );
    } finally {
      setSavingKey('');
    }
  }

  if (loading) {
  return (
    <div style={styles.muted}>
      Loading...
    </div>
  );
}

if (error) {
  return (
    <div style={styles.error}>
      {error}
    </div>
  );
}

if (instructors.length < 2) {
  return (
    <div style={styles.error}>
      This course is marked as having multiple instructors,
      but the instructor accounts could not be resolved.
    </div>
  );
}

  return (
    <div>
      <p style={styles.muted}>
        Divide course responsibilities between
        instructors. These settings are visible only
        to instructors.
      </p>

      <div style={styles.tabs}>
        {tabs.map(tab => (
          <button
  key={tab.key}
  type="button"
  style={{
    ...styles.tab,
    color:
      activeTab === tab.key
        ? '#215b7d'
        : '#66788a',
    boxShadow:
      activeTab === tab.key
        ? 'inset 0 -2px 0 #215b7d'
        : 'none'
  }}
  onClick={() =>
    setActiveTab(tab.key)
  }
>
  {tab.label}
</button>
        ))}
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}

      {activeTab === 'students' && (
        <div>
          <p style={styles.help}>
            Student assignments provide the default
            grading responsibility for that student.
          </p>

          {enrolments.length === 0 ? (
            <div style={styles.empty}>
              No active students.
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>
                      Student ID
                    </th>

                    <th style={styles.th}>
                      Student
                    </th>

                    <th style={styles.th}>
                      Instructor
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {enrolments.map(enrolment => (
                    <tr key={enrolment.id}>
                      <td style={styles.td}>
                        {
                          enrolment.students
                            ?.student_code
                        }
                      </td>

                      <td style={styles.td}>
                        {getStudentName(
                          enrolment.students
                        )}
                      </td>

                      <td style={styles.td}>
                        <select
                          style={styles.select}
                          value={
                            enrolment
                              .assigned_instructor_id ||
                            ''
                          }
                          disabled={
                            savingKey ===
                            `student-${enrolment.id}`
                          }
                          onChange={event =>
                            saveStudentInstructor(
                              enrolment.id,
                              event.target.value
                            )
                          }
                        >
                          <option value="">
                            None — use course default
                          </option>

                          {instructors.map(
                            instructor => (
                              <option
                                key={
                                  instructor.id
                                }
                                value={
                                  instructor.id
                                }
                              >
                                {instructor.name ||
                                  instructor.email}
                              </option>
                            )
                          )}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'classes' && (
  <div>
    <p style={styles.help}>
      Select the instructors teaching each class.
      All instructors are selected by default, and
      at least one instructor must remain selected.
    </p>

    {classWeeks.length === 0 ? (
      <div style={styles.empty}>
        No course weeks.
      </div>
    ) : (
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                Week
              </th>

              <th style={styles.th}>
                Date
              </th>

              <th style={styles.th}>
                Instructors
              </th>
            </tr>
          </thead>

          <tbody>
            {classWeeks.map(week => {
              const checkedIds =
                getClassInstructorIds(
                  week
                );

              const rowSaving =
                savingKey ===
                `class-${week.id}`;

              return (
                <tr key={week.id}>
                  <td style={styles.td}>
                    Week {week.week_number}
                  </td>

                  <td style={styles.td}>
                    {formatDate(
                      week.class_date
                    )}
                  </td>

                  <td style={styles.td}>
                    <div
                      style={
                        styles.classInstructorChecks
                      }
                    >
                      {instructors.map(
                        instructor => {
                          const checked =
                            checkedIds.includes(
                              instructor.id
                            );

                          return (
                            <label
                              key={
                                instructor.id
                              }
                              style={
                                styles.classInstructorCheck
                              }
                            >
                              <input
                                type="checkbox"
                                checked={
                                  checked
                                }
                                disabled={
                                  rowSaving
                                }
                                onChange={
                                  event =>
                                    toggleClassInstructor(
                                      week,
                                      instructor.id,
                                      event
                                        .target
                                        .checked
                                    )
                                }
                              />

                              <span>
                                {instructor.name ||
                                  instructor.email}
                              </span>
                            </label>
                          );
                        }
                      )}

                      {rowSaving && (
                        <span
                          style={
                            styles.classSaving
                          }
                        >
                          Saving...
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

      {activeTab === 'pd' && (
        <div>
          <p style={styles.help}>
            A Philosophical Diary with no override
            uses the student's assigned instructor.
          </p>

          {pdWeeks.length === 0 ? (
            <div style={styles.empty}>
              No Philosophical Diary weeks are
              currently enabled.
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>
                      Week
                    </th>

                    <th style={styles.th}>
                      Date
                    </th>

                    <th style={styles.th}>
                      Instructor
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pdWeeks.map(week => (
                    <tr key={week.id}>
                      <td style={styles.td}>
                        Week {week.week_number}
                      </td>

                      <td style={styles.td}>
                        {formatDate(
                          week.class_date
                        )}
                      </td>

                      <td style={styles.td}>
                        <select
                          style={styles.select}
                          value={
                            week
                              .pd_assigned_instructor_id ||
                            ''
                          }
                          disabled={
                            savingKey ===
                            `pd-${week.id}`
                          }
                          onChange={event =>
                            savePdInstructor(
                              week.id,
                              event.target.value
                            )
                          }
                        >
                          <option value="">
                            Use student assignment
                          </option>

                          {instructors.map(
                            instructor => (
                              <option
                                key={
                                  instructor.id
                                }
                                value={
                                  instructor.id
                                }
                              >
                                {instructor.name ||
                                  instructor.email}
                              </option>
                            )
                          )}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'assignments' && (
        <div>
          <p style={styles.help}>
            An assignment with no override uses each
            student's assigned instructor.
          </p>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    Assignment
                  </th>

                  <th style={styles.th}>
                    Instructor
                  </th>
                </tr>
              </thead>

              <tbody>
                {submissionAssignments.map(
                  assignment => (
                    <tr key={assignment.id}>
                      <td style={styles.td}>
                        {getAssignmentLabel(
  assignment,
  submissionAssignments
)}
                      </td>

                      <td style={styles.td}>
                        <select
                          style={styles.select}
                          value={
                            assignment
                              .assigned_instructor_id ||
                            ''
                          }
                          disabled={
                            savingKey ===
                            `assignment-${assignment.id}`
                          }
                          onChange={event =>
                            saveAssignmentInstructor(
                              assignment.id,
                              event.target.value
                            )
                          }
                        >
                          <option value="">
                            Use student assignment
                          </option>

                          {instructors.map(
                            instructor => (
                              <option
                                key={
                                  instructor.id
                                }
                                value={
                                  instructor.id
                                }
                              >
                                {instructor.name ||
                                  instructor.email}
                              </option>
                            )
                          )}
                        </select>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '0 0 18px',
    lineHeight: '1.5'
  },

  help: {
    color: '#66788a',
    fontSize: '13px',
    margin: '0 0 16px',
    lineHeight: '1.5'
  },

  tabs: {
    display: 'flex',
    gap: '4px',
    borderBottom: '2px solid #d7dee5',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },

  tab: {
    border: 'none',
    background: 'transparent',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#66788a',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px'
  },

  tabActive: {
    color: '#215b7d',
    borderBottomColor: '#215b7d'
  },

  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  success: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '8px 12px',
    color: '#216e45',
    fontSize: '13px',
    marginBottom: '16px'
  },

  empty: {
    color: '#66788a',
    fontSize: '13px',
    padding: '12px 0'
  },

  tableWrap: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    overflow: 'hidden'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },

  th: {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: '13px',
    color: '#42515f',
    background: '#f0f4f7',
    borderBottom: '1px solid #d7dee5'
  },

  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #edf1f4',
    fontSize: '14px',
    color: '#1f2933'
  },

  select: {
    width: '100%',
    minWidth: '190px',
    border: '1px solid #d7dee5',
    borderRadius: '7px',
    padding: '7px 9px',
    fontSize: '13px',
    background: 'white',
    color: '#1f2933'
  },
  classInstructorChecks: {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  flexWrap: 'wrap'
},

classInstructorCheck: {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13px',
  color: '#1f2933',
  cursor: 'pointer',
  whiteSpace: 'nowrap'
},

classSaving: {
  fontSize: '11px',
  color: '#66788a'
},
};